import type React from 'react'
import type {
  CMSButtonLink,
  CMSComponentResponsiveImage,
  CMSComponentVideo,
  CMSLinkUrl,
  CMSSectionTheme,
} from '@/types/sanity'
import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { Video } from '@/components/Shared/Video'
import { LinkButton } from '@/components/Link/LinkButton'
import styles from './FullBleedVariant.module.scss'
import {
  PortableText,
  type PortableTextBlock,
  type PortableTextReactComponents,
} from '@portabletext/react'
import { RichTextBlock } from '../SectionRichText/RichTextBlock'
import { Wrapper } from './FullBleedHeroBannerWrapper.tsx'

export type FullBleedConfig = {
  bannerType: 'fullBleed' | 'inset' | 'legacy'
  eyebrow?: string
  eyebrowColor?: string
  header?: string
  description?: PortableTextBlock[]
  cta?: CMSButtonLink
  theme?: CMSSectionTheme
  contentAlignment: 'left' | 'center'
  fullBleedContentPosition?: 'left' | 'center' | 'right'
  moduleUrl?: CMSLinkUrl
  isModuleClickable?: boolean
  content: {
    image?: CMSComponentResponsiveImage
    video?: CMSComponentVideo
  }
}

const portableTextComponents: Partial<PortableTextReactComponents> = {
  block: RichTextBlock,
  types: {},
}

export const FullBleedVariant: React.FC<{
  config: FullBleedConfig
}> = ({ config }) => {
  const {
    eyebrow,
    eyebrowColor,
    header,
    description,
    cta,
    content,
    contentAlignment,
    fullBleedContentPosition,
    isModuleClickable,
    moduleUrl,
  } = config

  return (
    <Wrapper
      isModuleClickable={isModuleClickable}
      moduleUrl={moduleUrl}
      content={content}
    >
      <div className={styles.container}>
        <div className={styles.backgroundImage}>
          {content?.video ? (
            <Video
              videoUrl={content.video.video.secureUrl}
              videoType={content.video.videoType}
              ratios={content.video.ratios}
              dialogueThumbnail={content.video.dialogue?.thumbnail}
              includeControls={content.video.dialogue?.includeControls}
              loopVideo={content.video.background?.loopVideo}
              captionsUrl={content.video.dialogue?.captionsUrl}
              backupImage={content.video.backupImage}
              containerClasses={styles.media}
              classes={styles.video}
            />
          ) : content?.image ? (
            <ResponsiveImage
              document={content.image}
              overrides={{
                customSizes: [1200, 4200],
              }}
            />
          ) : null}
        </div>
        {eyebrow || header || description || (cta && cta.text) ? (
          <div
            className={styles.content}
            data-position={
              fullBleedContentPosition || contentAlignment || 'center'
            }
            data-text={contentAlignment || 'left'}
          >
            <div className={styles.contentInner}>
              {eyebrow && (
                <p
                  className="type-label mb-16 md:mb-24"
                  style={{ color: eyebrowColor || undefined }}
                >
                  {eyebrow}
                </p>
              )}
              {header && (
                <h1
                  className="type-h1 md:type-h1 mb-32"
                  dangerouslySetInnerHTML={{ __html: header! }}
                />
              )}
              {description && (
                <div
                  className="mb-32 md:mb-40"
                  style={{ '--text-align': contentAlignment } as any}
                >
                  <PortableText
                    value={description}
                    components={portableTextComponents}
                  />
                </div>
              )}
              {cta && cta.text && <LinkButton link={cta} />}
            </div>
          </div>
        ) : null}
      </div>
    </Wrapper>
  )
}
